// Shared peer-name resolution for every call-facing surface (in-call title,
// incoming-call screen, Main's Recents, contact detail). A UUID is never a
// substitute for a name: on a cache miss (not a friend, or the friends
// cache/list hasn't hydrated yet — e.g. a cold-start accept deep link racing
// the first `listFriends()` fetch) this renders the translated "Unknown
// caller" copy instead of the raw account id, which would otherwise leak an
// opaque identifier onto a screen a caller is meant to trust at a glance.

import type { Friend } from '@merkury/protocol';
import { getLang, translate } from '../../i18n/index.js';
import { getAliases } from '../../storage/kvStores.js';
import { getCachedFriends } from '../../storage/serverCache.js';

/**
 * The single display rule (docs/08 §3): the observer's private alias for a
 * friend wins over the friend's own display name. Pure — pass the current
 * alias map (e.g. from `useAliases()`) so row rendering re-runs on change.
 * An alias only ever exists for a real friend (it is deleted on unfriend), so
 * this never resurrects a stale label.
 */
export function effectiveName(
  friend: { userId: string; displayName: string },
  aliases: Readonly<Record<string, string>>,
): string {
  const alias = aliases[friend.userId];
  return alias && alias.length > 0 ? alias : friend.displayName;
}

/**
 * Resolve `userId` to a display name. The device-local alias (docs/08) is
 * consulted FIRST — an aliased friend always shows the observer's private
 * label. Otherwise `friends`, when passed, is checked first — a screen's own
 * live `listFriends()` fetch is more current than the persisted cache
 * snapshot; omit it (e.g. the shell's return-to-call banner, which has no
 * friends list of its own) to resolve from the cache alone. Both a missing
 * `userId` and a genuine miss in both sources fall back to `snapshot` — a name
 * recorded at call time (the call log's `peerName`), which outlives the
 * friendship — and only then to the translated unknown-caller string — never
 * the raw id.
 */
export function displayNameFor(
  userId: string | null | undefined,
  friends?: readonly Friend[],
  snapshot?: string | null,
): string {
  if (!userId) return translate(getLang(), 'call.unknownCaller');
  const alias = getAliases()[userId];
  if (alias) return alias;
  const live = friends?.find((f) => f.userId === userId)?.displayName;
  if (live) return live;
  const cached = getCachedFriends()?.data.find((f) => f.userId === userId)?.displayName;
  return cached ?? snapshot ?? translate(getLang(), 'call.unknownCaller');
}
